import { TemplateResponseData } from '@/services/inference.gen'
import { ServerTemplateCard } from '../DashboardTemplates/ServerTemplateCard'
import { LoadingMessage } from '@/components/loading-message'
import { ErrorMessage } from '@/components/error-message'
import { Button } from '@/components/ui/button'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type TemplateSelectionProps = {
  templates: TemplateResponseData[] | undefined
  isLoading: boolean
  isError: boolean
  selectedTemplate: TemplateResponseData | null
  onTemplateSelect: (template: TemplateResponseData) => void
  onNext: () => void
}

export const TemplateSelection = ({
  templates,
  isLoading,
  isError,
  selectedTemplate,
  onTemplateSelect,
  onNext,
}: TemplateSelectionProps) => {
  if (isLoading) {
    return (
      <div className="relative min-h-[400px]">
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingMessage message="Loading templates..." centered />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="relative min-h-[400px]">
        <div className="flex items-center justify-center min-h-[400px]">
          <ErrorMessage message="Failed to load templates. Please try again." />
        </div>
      </div>
    )
  }

  if (!templates || templates.length === 0) {
    return (
      <div className="relative min-h-[400px]">
        <div className="flex flex-col items-center justify-center min-h-[400px] bg-muted/30 rounded-lg p-8 text-center">
          <p className="text-lg font-medium mb-2">No templates available</p>
          <p className="text-sm text-muted-foreground">
            Please contact your administrator to set up server templates
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-[400px]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1
            className="text-3xl font-bold mb-2"
            data-testid="template-selection-title"
          >
            Choose A Template
          </h1>
          <p className="text-muted-foreground">
            Select a template to configure your new server instance
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          {templates.length} template{templates.length !== 1 ? 's' : ''}{' '}
          available
        </p>
      </div>

      <div
        className={cn(
          'grid gap-6',
          templates.length === 1 ? 'max-w-4xl' : 'grid-cols-1 lg:grid-cols-2'
        )}
        data-testid="template-grid"
      >
        {templates.map((template) => (
          <ServerTemplateCard
            key={template.template_id}
            id={String(template.template_id)}
            name={template.template_name}
            specs={{
              cpu: {
                count: Number(template.cpu_limit_count),
                ramGb: Number(template.ram_limit_gb),
              },
              gpu: {
                count: Number(template.gpu_request_count),
                memoryGb: Number(template.gpu_request_gmem),
              },
              machineImage: template.machine_image,
              storage: {
                ephemeralGb: Number(template.ephemeral_storage_limit_gb || 0),
              },
            }}
            price={parseFloat((template.price / 100).toFixed(2))}
            selected={selectedTemplate === template}
            onSelect={() => onTemplateSelect(template)}
            data-testid={`template-card-${template.template_id}`}
          />
        ))}
      </div>

      {selectedTemplate && (
        <div className="sticky bottom-5 mt-8 pb-2 z-20">
          <div className="flex justify-start pointer-events-none">
            <div className="pointer-events-auto flex items-center bg-card border rounded-lg shadow-lg p-4 animate-in fade-in slide-in-from-bottom-5">
              <div className="mr-4">
                <div className="text-sm text-muted-foreground mb-1">
                  Selected Template
                </div>
                <div className="font-medium">
                  <span data-testid="server-name-display">
                    {selectedTemplate.template_name}
                  </span>
                </div>
              </div>
              <Button
                type="button"
                onClick={onNext}
                className="ml-2"
                data-testid="configure-button"
              >
                Configure <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
